import { 
  selectEmails,
  selectEmailsValidation,
  selectFetchingMetadata,
  selectFetchingReport,
  selectMetadata,
  selectUsePredefDate,
  selectValidateReportFilters,
  selectScheduleDefinition,
  selectFetchingScheduleDefinition,
  selectScheduleDefinitionChanged,
  selectScheduleFailedConstraints,
  selectFetchingSpecialReport
} from 'app/area/company-report/selectors';
import { useInstanceKey } from 'app/area/favourite/favourite-context';
import { 
  selectScheduleDefinitionId,
  selectPredefDateForSelectedReport,
  selectWorkingDefinition,
  selectFetchingDefinition
} from 'app/area/favourite/selectors';
import { useCallback, useEffect, useMemo, type JSX } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import { actionCreators } from './actions';
import { useActionCreators as useFavouriteActionCreators } from 'app/area/favourite/hooks';
import { isValidListOfEmails } from 'app/infrastructure/validation/email';
import { ScheduleDefinition } from 'app/area/company-report/types-store';
import { selectFilterDomains } from 'app/area/filters/selectors';
import { useSharedState } from 'app/infrastructure/redux/hooks';

export function useActionCreators() {
  const dispatch = useDispatch();
  return useMemo(() => 
    bindActionCreators(actionCreators, dispatch), [dispatch]
  );
}

export function useSelectors() {
  const instanceKey = useInstanceKey();
  const metadata = useSelector(selectMetadata);
  const emails = useSelector(selectEmails);
  const fetchingReport = useSelector(selectFetchingReport);
  const fetchingMetadata = useSelector(selectFetchingMetadata);
  const emailsValidation = useSelector(selectEmailsValidation);
  const validateReportFilters = useSelector(selectValidateReportFilters);
  const scheduleDefinition = useSelector(selectScheduleDefinition);
  const scheduleDefinitionId = useSelector(selectScheduleDefinitionId(instanceKey));
  const fetchingScheduleDefinition = useSelector(selectFetchingScheduleDefinition);
  const scheduleDefinitionChanged = useSelector(selectScheduleDefinitionChanged);
  const scheduleFailedConstraints = useSelector(selectScheduleFailedConstraints);
  const fetchingSpecialReport = useSelector(selectFetchingSpecialReport);

  return {
    metadata,
    emails,
    fetchingReport,
    fetchingMetadata,
    emailsValidation,
    validateReportFilters,
    scheduleDefinition,
    scheduleDefinitionId,
    fetchingScheduleDefinition,
    scheduleDefinitionChanged,
    scheduleFailedConstraints,
    fetchingSpecialReport
  };
}

export function useIsRequiredFilterDomainsNotEmpty() {
  const instanceKey = useInstanceKey();
  const workingDefinition = useSelector(selectWorkingDefinition(instanceKey));
  const filterDomains = useSelector(selectFilterDomains(instanceKey));
  const filterDomainsRequired = filterDomains?.filter?.(row => row.required === true).map(row => row.domain);
  const filterDomainsNotEmpty = workingDefinition?.conditions?.map(row => row.domain.v);
    
  return filterDomainsRequired?.every(val=>filterDomainsNotEmpty?.includes(val));
}

export function useIsReportDisabled(definition?: any) {
  const companyReportDefinition = definition?.reports?.filter(report=>report.report.v === definition.selectedReport.v)[0];
  const reportDisabledDate = new Date(companyReportDefinition?.specialReport?.disabled);
  if(reportDisabledDate?.getTime() <= new Date().getTime()) {
    return true;
  }
  return false;
}

export function useCanSaveReport() {
  const instanceKey = useInstanceKey();
  const predefDate = useSelector(selectPredefDateForSelectedReport(instanceKey));
  const usePredefDate = useSelector(selectUsePredefDate);
  const predefDateNotEmpty =  !usePredefDate || predefDate;
  const requiredFilterDomainsNotEmpty = useIsRequiredFilterDomainsNotEmpty();

  return predefDateNotEmpty && requiredFilterDomainsNotEmpty;
}

export function useSaveSendReportCallbacks(onSaveSuccess: () => JSX.Element) {
  const {
    sendReport,
    saveReport,
    validateReportFilters
  } = useActionCreators();

  const instanceKey = useInstanceKey();
  const canSaveReport = useCanSaveReport();
  const emails = useSelector(selectEmails);
  const workingDefinition = useSelector(selectWorkingDefinition(instanceKey));

  const onSendReport = useCallback(() => {
    canSaveReport ? 
      sendReport(emails, workingDefinition, onSaveSuccess) : 
      validateReportFilters();
  }, [canSaveReport, sendReport, emails, workingDefinition, onSaveSuccess, validateReportFilters]);

  const onSaveReport = useCallback(() => {
    canSaveReport ?
      saveReport(workingDefinition, onSaveSuccess) : 
      validateReportFilters();
  }, [canSaveReport, workingDefinition, onSaveSuccess, saveReport, validateReportFilters]);

  return {
    onSendReport,
    onSaveReport
  };
}

export function useScheduleDefinitionCallback(favouriteId: number) {
  const {
    deleteScheduleDefinition,
    updateScheduleDefinition,
    createScheduleDefinition
  } = useActionCreators();

  const instanceKey = useInstanceKey();
  const workingDefinition = useSelector(selectWorkingDefinition(instanceKey));
  const scheduleDefinition = useSelector(selectScheduleDefinition);
  const scheduleWorkingDefinitionChanged = useSelector(selectScheduleDefinitionChanged);

  const onDeleteScheduleDefinition = useCallback(() => {
    deleteScheduleDefinition(workingDefinition?.favourite?.v, scheduleDefinition.scheduleDefinitionId);
  }, [workingDefinition, scheduleDefinition]);

  const onUpdateScheduleDefinition = useCallback((workingScheduleDefinition: ScheduleDefinition, validateSchedule: boolean = false) => {
    updateScheduleDefinition(workingScheduleDefinition, favouriteId, validateSchedule);
  }, [scheduleDefinition, favouriteId]);

  const onCreateScheduleDefinition = useCallback(() => {
    createScheduleDefinition(favouriteId);
  }, [scheduleDefinition, favouriteId]);

  const onUpdateScheduleDefinitionOnSubmit = useCallback(async() => {
    scheduleWorkingDefinitionChanged && (await updateScheduleDefinition(scheduleDefinition, favouriteId, true));
  }, [scheduleDefinition, favouriteId]);

  return {
    onDeleteScheduleDefinition,
    onCreateScheduleDefinition,
    onUpdateScheduleDefinition,
    onUpdateScheduleDefinitionOnSubmit
  };
}

export function useKeyboardHandlers(companyReportId: number, onSaveSuccess: () => JSX.Element) {
  const {
    sendReport
  } = useActionCreators();

  const {
    metadata,
    emails
  } = useSelectors();
  
  const instanceKey = useInstanceKey();
  const workingDefinition = useSelector(selectWorkingDefinition(instanceKey));

  const onEnter = useCallback((event: React.KeyboardEvent<HTMLElement>) => {
    const { eMailReport } = metadata;
    event.preventDefault();
    !!eMailReport && sendReport(emails, workingDefinition, onSaveSuccess);
  }, [metadata, emails, companyReportId, workingDefinition, onSaveSuccess]);

  return useMemo(() => ({
    onEnter
  }), [onEnter]);
}

export function useLoadMetadataEffect(companyReportId: number) {
  const {
    loadMetadata,
    clearForm
  } = useActionCreators();

  useEffect(() => {
    loadMetadata(companyReportId);
    return () => {
      clearForm();
    };
  }, [companyReportId]);
}

export function useLoadScheduleEffect(favouriteId: number) {
  const {
    getScheduleDefinition
  } = useActionCreators();

  const { fetchingScheduleDefinition } = useSelectors();

  useEffect(() => {
    if (fetchingScheduleDefinition || favouriteId === undefined) {
      return;
    }
    getScheduleDefinition(favouriteId);
  }, [favouriteId]);
}

export function useScheduleDefinition() {
  const { scheduleDefinition, fetchingScheduleDefinition, scheduleDefinitionChanged, scheduleFailedConstraints } = useSelectors();

  return { 
    scheduleDefinition,
    fetchingScheduleDefinition,
    scheduleDefinitionChanged,
    scheduleFailedConstraints
  };
}

export function useTemplateFileHandlers(companyReportId: number, onClose: () => void) {
  const {
    updateTemplate,
    downloadTemplate
  } = useActionCreators();

  const handleUploadTemplate = useCallback((file: File) => {
    updateTemplate(companyReportId, file, onClose);
  }, [companyReportId]);

  const handleDownloadTemplate = useCallback(() => {
    downloadTemplate(companyReportId, 'template.xlsm');
  }, [companyReportId]);

  return {
    handleUploadTemplate,
    handleDownloadTemplate
  };
}

export function useCompanyReportFavouriteEffect(companyReportId: number, favouriteId: number,
    eMailReport: boolean, fetchingMetadata: boolean) {
  const {
    createCompanyReportDefinition
  } = useFavouriteActionCreators();
  const userEmail = useSharedState()?.email;
  const fetchingSpecialReport = useSelector(selectFetchingSpecialReport);

  useEffect(() => {

    if(fetchingSpecialReport) {
      return;
    }

    createCompanyReportDefinition(companyReportId, favouriteId, eMailReport, userEmail);
  }, [companyReportId, favouriteId, fetchingSpecialReport]);
}

export function useEmailSubscribersToCompanyReportDefinitionEffect(
  emails: string,
  eMailReport: boolean
) {
  const instanceKey = useInstanceKey();
  const fetchingDefinition = useSelector(selectFetchingDefinition(instanceKey));

  const {
    transformWorkingDefinition
  } = useFavouriteActionCreators();

  useEffect(() => {
    
    if (fetchingDefinition || !eMailReport || (!!emails && !isValidListOfEmails(emails))) {
      return;
    }
    
    transformWorkingDefinition(definition => {

      return {
        ...definition,
        emailSubscribers: emails
      };
    });
  }, [fetchingDefinition, emails, transformWorkingDefinition]);
}
